import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

/**
 * A connection that stopped, in the Alerts bell (api healthWatch: the
 * meta_token_invalid, spend_sync_stale, buyer_scoring_stale,
 * shopify_app_uninstalled and ai_calling_paused checks). AI calling paused
 * reads its own title ("AI calling paused: the Bolna wallet is empty"),
 * which says why, and links to Resume on the Calls page.
 *
 * Expired Meta tokens failed silently for three workspaces (Oct 2026): spend
 * stopped and the first anyone knew was a report short of it. The health watch
 * now says so by email; this is the same incident on every screen, from its
 * first check, with the one link that fixes it. A check that is ours to fix
 * (buyer scores) is listed as status, not counted as the client's to act on.
 */
const TEXT = {
  meta_token_invalid: 'Meta disconnected: the access token stopped working',
  spend_sync_stale: 'Ad spend stopped syncing, so return numbers read low',
  buyer_scoring_stale: 'Buyer scores stopped updating; we are on it',
  shopify_app_uninstalled: 'The Cookiebees app was uninstalled from your Shopify store',
};

/** Pure: GET /api/health-watch/connections' alerts as bell rows. */
export function connectionRows(alerts) {
  return (Array.isArray(alerts) ? alerts : []).map((a) => ({
    id: `connection-${a.check_key}`,
    tone: a.ours ? 'info' : a.severity === 'critical' ? 'bad' : 'warn',
    text: TEXT[a.check_key] || a.title,
    to: a.action?.href || '/tracking/health',
    linkLabel: a.action?.label || 'What we saw',
  }));
}

export function useConnectionAlerts() {
  const { data } = useQuery({
    queryKey: ['health-watch', 'connections'],
    queryFn: async () => (await api.get('/api/health-watch/connections')).data,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
  return connectionRows(data?.alerts);
}
